import { useState, useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  IconButton,
  TextField,
  Box,
  Typography,
  Grid2,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { useTranslation } from "react-i18next";
import { useAuth } from "oidc-react";
import { useToastState } from "../ToastStateProvider";

const EditTimeDialog = ({ open, onClose, timeReportItem, onSave }) => {
  const { t } = useTranslation();
  const [quantity, setQuantity] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const auth = useAuth();
  const accessToken = auth.userData?.access_token;
  const rootUrl = import.meta.env.VITE_BACKEND_API_ROOT_URL;
  const { setToastState } = useToastState();

  const showToast = (message, severity) => {
    setToastState({ message, severity, open: true });
  };

  useEffect(() => {
    if (timeReportItem && timeReportItem.hours) {
      setQuantity(timeReportItem.hours);
    }
  }, [timeReportItem]);

  const increaseQuantity = () => {
    setQuantity((prev) => prev + 0.25);
  };

  const decreaseQuantity = () => {
    if (quantity > 0.25) {
      setQuantity((prev) => prev - 0.25);
    }
  };
  const handleSave = async () => {
    if (!timeReportItem || quantity <= 0) return;

    setIsSubmitting(true);

    try {
      const response = await fetch(
        `${rootUrl}timereport/workorder/${timeReportItem.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            woNo: timeReportItem.workOrderNumber,
            hours: quantity,
            rowNo: timeReportItem.rowNo,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update time report");
      }

      showToast(t("timeReport.editTimeDialog.successMessage"), "success");

      if (onSave) {
        onSave();
      }
      onClose();
    } catch (error) {
      console.error("Error updating time report:", error);
      showToast(t("timeReport.editTimeDialog.errors.submitError"), "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };
  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          onClose();
        }
      }}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>{t("timeReport.editTimeDialog.workOrderTitle")}</DialogTitle>
      <DialogContent>
        {timeReportItem && (
          <>
            <Typography variant="body1" sx={{ mb: 2, fontWeight: "bold" }}>
              {`${t("workOrder.id")} ${timeReportItem.workOrderNumber} - ${
                timeReportItem.name
              }`}
            </Typography>

            {/* Quantity Input */}
            <Box sx={{ width: "100%", mt: 2 }}>
              <Grid2 container alignItems="center" spacing={1}>
                <Grid2 item>
                  <IconButton
                    onClick={decreaseQuantity}
                    disabled={quantity <= 0.25}
                  >
                    <RemoveIcon />
                  </IconButton>
                </Grid2>
                <Grid2 item>
                  <TextField
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(0, +e.target.value))}
                    inputProps={{ step: 0.25, min: 0 }}
                    sx={{ width: "85px", textAlign: "center" }}
                    autoFocus
                  />
                </Grid2>
                <Grid2 item>
                  <IconButton onClick={increaseQuantity}>
                    <AddIcon />
                  </IconButton>
                </Grid2>
              </Grid2>
              <Box sx={{ mt: 1 }}>
                {(() => {
                  const hours = Math.floor(quantity);
                  const minutes = Math.round((quantity - hours) * 60);
                  return t("timeReport.addTimeDialog.duration", {
                    hours,
                    minutes,
                    defaultValue: `${hours} hours and ${minutes} minutes`,
                  });
                })()}
              </Box>
            </Box>
          </>
        )}{" "}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="secondary">
          {t("close")}
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          disabled={isSubmitting || !timeReportItem || quantity <= 0}
        >
          {t("change")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditTimeDialog;
